import React, { useCallback, useContext, useEffect, useState } from "react";

import { useTheme } from "emotion-theming";
import { ApplicationContext } from "wrapper/ApplicationWrapper/context";
import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { getPlatform } from "../../../helpers";
import { giftCardBalance } from "store/payment/services";
import { ErrorModalContext } from "wrapper/ErrorModalWrapper/context";
import { PAYMENT_ERROR_MESSAGE } from "constants/paymentStatus";
import { validateGiftCardNumberField } from "helpers/utils/validateGiftCardNumberField";
import { validateGiftCardPinField } from "helpers/utils/validateGiftCardPinField";
import { getStringFromId } from "helpers/vernacular";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { PAYMENT_MODES } from "constants/paymentModes";
import { ACCORDIAN, DEVICE_TYPE } from "constants/index";

import GiftCardDesktop from "@nykaa/payment-sdk/src/GiftCard/desktop";
import GiftCard from "@nykaa/payment-sdk/src/GiftCard/mobile";
import { doHandleOtherPaymentClick } from "../common/dependencies/dependencyhelpers";


interface IgiftcardBalanceFailureResponse {
  success: boolean,
  message: string,
  statusCode: string,
  data: {
    amount: number,
    expiry: string,
  }
}

interface IgiftcardBalanceSuccessResponse {
  amount: number,
  expiry: string,
}

type IgiftcardBalanceResponse = IgiftcardBalanceFailureResponse | IgiftcardBalanceSuccessResponse;

const GiftcardWrapper = ({
  doPayment,
  isAccordianOpened,
  toggleNewPaymentAccordian,
  endIcon,
  isDisabled,
  contentRefs,
  accordian,
  newPaymentMethodWithPriority,
  newPaymentMethod,
}: any) => {
  const paymentContext: any = useContext(PaymentContext);
  const { applicationData } = useContext(ApplicationContext);
  const errorModalContext = useContext(ErrorModalContext);

  const theme = useTheme() as Theme;
  const platform = getPlatform();

  const grandTotal = paymentContext?.orderAmount;
  const walletConfig = paymentContext?.walletConfig;
  const giftCardConfig = paymentContext?.giftCardInfoConfig;
  const setLoading = paymentContext?.setLoading;

  const deviceType = applicationData?.deviceType;
  const openErrorModal = errorModalContext?.openErrorModal;

  const { isGiftCardApplied, giftCard, setGiftCard, setIsGiftCardApplied } = giftCardConfig;
  const { isWalletChecked, walletBalance } = walletConfig;

  const finalOrderAmount = grandTotal;

  const [giftCardNumber, setGiftCardNumber] = useState({
    value: giftCard?.giftCardNumber || "",
    hasError: false,
    error: "",
  });
  const [giftCardPin, setGiftCardPin] = useState({
    value: giftCard?.pin || "",
    hasError: false,
    error: "",
  });

  const [balance, setGiftCardBalance] = useState(
    giftCard?.giftCardBalance || 0
  );
  /*
  this useEffect gets the focus on the first input field once user opens the accordian
  */

  useEffect(() => {
    let giftCardInputFieldAutoFocus = contentRefs?.current[PAYMENT_MODES.GIFT_CARD_MSITE]?.getElementsByClassName("input-element")[0];
    if (giftCardInputFieldAutoFocus && isAccordianOpened) {
      setTimeout(() => {
        giftCardInputFieldAutoFocus?.focus({ preventScroll: true });
      }, 20);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isAccordianOpened]);

  /*
    this useEffect gets the focus on the first input field once user opens the accordian in desktop
  */

  useEffect(() => {
      if (deviceType === DEVICE_TYPE.DESKTOP && accordian === ACCORDIAN.GIFT_CARD) {
          let giftCardInputFieldAutoFocus = contentRefs?.current[ACCORDIAN.GIFT_CARD]?.getElementsByClassName(
              "input-element"
            )[0];
            if (giftCardInputFieldAutoFocus && isAccordianOpened) {
              setTimeout(() => {
                giftCardInputFieldAutoFocus?.focus({ preventScroll: true });
              }, 20);
            }
      }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [accordian]);

  const handleApplyGiftCard = async () => {
    const giftCardApiParams = {
      cardNumber: giftCardNumber.value,
      cardPin: giftCardPin.value,
    };
    return giftCardBalance(giftCardApiParams).then((res: IgiftcardBalanceResponse) => {
      // add logic for setting giftcard Data
      const amount = (res as IgiftcardBalanceSuccessResponse)?.amount;
      let message = (res as IgiftcardBalanceFailureResponse)?.message;

      if (!amount || message) {
        if (amount === 0) {
          message = "Gift Card balance is 0";
        }
        openErrorModal(PAYMENT_ERROR_MESSAGE.FAILED, message);
        return;
      }
      let amountUsed = amount;
      if (amount > grandTotal) amountUsed = grandTotal;
      setGiftCard({
        giftCardAmountUsed: amountUsed,
        giftCardNumber: giftCardNumber.value,
        pin: giftCardPin.value,
        giftCardBalance: amount,
      });
      setIsGiftCardApplied(true);
      setGiftCardBalance(amount);
    });
  };

  /**
   * Function for removing gift card and clear the all applied gift card and clear all the fields.
   */
   const handleRemoveGiftCard = async () => {
    // logic for removing giftcard
    setIsGiftCardApplied(false);

    setGiftCardNumber({
      value: "",
      hasError: false,
      error: "",
    });
    setGiftCardPin({
      value: "",
      hasError: false,
      error: "",
    });

    setGiftCard({
      giftCardAmountUsed: 0,
      giftCardNumber: "",
      pin: "",
      giftCardBalance: 0,
    });
  };

  const validateInputs = useCallback(
    (key?: string, value?: string) => {
      let invalidSubmitOccured = false;
      let giftCardNumberStatus = null;
      let giftCardPinStatus = null;
      switch (key) {
        case "giftCardNumber":
          if (value) {
            giftCardNumberStatus = validateGiftCardNumberField(value);
          } else {
            giftCardNumberStatus = validateGiftCardNumberField(
              giftCardNumber.value
            );
          }
          if (giftCardNumberStatus.hasError !== giftCardNumber.hasError) {
            setGiftCardNumber({
              value: value ? value : giftCardNumber.value,
              error: giftCardNumberStatus.error,
              hasError: giftCardNumberStatus.hasError,
            });
            invalidSubmitOccured = true;
          }
          break;

        case "giftCardPin":
          giftCardPinStatus = validateGiftCardPinField(giftCardPin.value);
          if (giftCardPinStatus.hasError !== giftCardPin.hasError) {
            setGiftCardPin({
              value: value ? value : giftCardPin.value,
              error: giftCardPinStatus.error,
              hasError: giftCardPinStatus.hasError,
            });
            invalidSubmitOccured = true;
          }
          break;

        default:
          giftCardNumberStatus = validateGiftCardNumberField(
            giftCardNumber.value
          );
          giftCardPinStatus = validateGiftCardPinField(giftCardPin.value);
          if (giftCardNumberStatus.hasError !== giftCardNumber.hasError) {
            invalidSubmitOccured = true;
            setGiftCardNumber({
              value: giftCardNumber.value,
              error: giftCardNumberStatus.error,
              hasError: giftCardNumberStatus.hasError,
            });
          }
          if (giftCardPinStatus.hasError !== giftCardPin.hasError) {
            invalidSubmitOccured = true;
            setGiftCardPin({
              value: giftCardPin.value,
              error: giftCardPinStatus.error,
              hasError: giftCardPinStatus.hasError,
            });
          }
          break;
      }
      return invalidSubmitOccured;
    },
    [giftCardNumber, giftCardPin]
  );

  const handleGiftCardSubmit = () => {
    setLoading(true);
    const hasInvalidSubmitOccurred = validateInputs();
    if (
      hasInvalidSubmitOccurred ||
      giftCardPin.hasError ||
      giftCardNumber.hasError
    ) {
      setLoading(false);
      return;
    }
    if (isGiftCardApplied) {
      handleRemoveGiftCard().then(() => {
        setLoading(false);
      }).catch(() => {
        openErrorModal(PAYMENT_ERROR_MESSAGE.SERVER_ERROR, PAYMENT_ERROR_MESSAGE.SOMETHING_WENT_WRONG);
        setLoading(false);
      });
    } else {
      handleApplyGiftCard().then(() => {
        setLoading(false);
      }).catch(() => {
        openErrorModal(PAYMENT_ERROR_MESSAGE.SERVER_ERROR, PAYMENT_ERROR_MESSAGE.SOMETHING_WENT_WRONG);
        setLoading(false);
      });
    }
  };

  const handleInputChange = (inputValue: string, key: string) => {
    let value = inputValue;
    
    if (value.length < 0) {
      return;
    }
    if (key === "giftCardNumber" || key === "giftCardPin") {
      value = value.replace(/[^0-9]/g, "");
      switch (key) {
        case "giftCardNumber":
          if (value.length > 16) return;
          /*
          to move the focus to the next input field once user enters the giftcard number
          */
          if (value.length === 16 ) {
            validateInputs(key, value);
            if (!giftCardNumber.hasError) {
              let gitCardNumberField: any = document.getElementById("gift-card-pin");
              setTimeout(() => {
                gitCardNumberField?.focus({ preventScroll: true });
              }, 100);
            }
          }
          setGiftCardNumber({
            value,
            error: "",
            hasError: false,
          });
          break;
        case "giftCardPin":
          if (value.length > 6) return;
          setGiftCardPin({
            value,
            error: "",
            hasError: false,
          });
          break;
      }
    }
  };

  /**
   * this function is to switch to the first new payment method
   * if giftcard balance is lower then grandTotal
   * if cod on first place then show second
   * payment option to user.
   */
  const doPaymentWithOtherMethod = () => {
    doHandleOtherPaymentClick({
      deviceType,
      paymentMethodDesktop: newPaymentMethod,
      paymentMethodMsite: newPaymentMethodWithPriority,
      callBackForDesktop: toggleNewPaymentAccordian,
      callBackForMsite: toggleNewPaymentAccordian,
      currentPaymentOption: ACCORDIAN.GIFT_CARD,
    });
    return;
  };

  const { giftCardAmountUsed } = giftCard;
  const giftCardBalanceRemaining = balance - giftCardAmountUsed;

  let title = getStringFromId(
    VERNACULAR_STRING_IDS.GIFT_CARD_TITLE,
    VERNACULAR_DEFAULT_MESSAGES.GIFT_CARD_TITLE
  );
  if (deviceType === DEVICE_TYPE.DESKTOP) {
    title = getStringFromId(
      VERNACULAR_STRING_IDS.GIFT_CARD_TITLE_DESKTOP,
      VERNACULAR_DEFAULT_MESSAGES.GIFT_CARD_TITLE_DESKTOP
    );
  }
  const subTitle = getStringFromId(
    VERNACULAR_STRING_IDS.GIFT_CARD_SUBTITLE,
    VERNACULAR_DEFAULT_MESSAGES.GIFT_CARD_SUBTITLE
  );
  const placeOrderText = getStringFromId(
    VERNACULAR_STRING_IDS.PLACE_ORDER,
    VERNACULAR_DEFAULT_MESSAGES.PLACE_ORDER
  );
  const remove = getStringFromId(
    VERNACULAR_STRING_IDS.REMOVE,
    VERNACULAR_DEFAULT_MESSAGES.REMOVE
  );
  const payNowText = getStringFromId(
    VERNACULAR_STRING_IDS.PAY_NOW,
    VERNACULAR_DEFAULT_MESSAGES.PAY_NOW
  );
  const submitText = getStringFromId(
    VERNACULAR_STRING_IDS.PROCEED,
    VERNACULAR_DEFAULT_MESSAGES.PROCEED
  );
  const walletUsedText = getStringFromId(
    VERNACULAR_STRING_IDS.NYKAA_WALLET_USED,
    VERNACULAR_DEFAULT_MESSAGES.NYKAA_WALLET_USED
  );
  const giftCardNumberLabelText = getStringFromId(
    VERNACULAR_STRING_IDS.GIFT_CARD_NUMBER,
    VERNACULAR_DEFAULT_MESSAGES.GIFT_CARD_NUMBER
  );
  const giftCardPinLabelText = getStringFromId(
    VERNACULAR_STRING_IDS.GIFT_CARD_PIN,
    VERNACULAR_DEFAULT_MESSAGES.GIFT_CARD_PIN
  );
  const giftCardValueText = getStringFromId(
    VERNACULAR_STRING_IDS.GIFT_CARD_VALUE,
    VERNACULAR_DEFAULT_MESSAGES.GIFT_CARD_VALUE
  );
  const giftCardAmountUsedText = getStringFromId(
    VERNACULAR_STRING_IDS.GIFT_CARD_VALUE_USED,
    VERNACULAR_DEFAULT_MESSAGES.GIFT_CARD_VALUE_USED
  );
  const giftCardBalanceRemainingText = getStringFromId(
    VERNACULAR_STRING_IDS.GIFT_CARD_BALANCE,
    VERNACULAR_DEFAULT_MESSAGES.GIFT_CARD_BALANCE
  );
  const amountPayableText = getStringFromId(
    VERNACULAR_STRING_IDS.AMOUNT_PAYABLE,
    VERNACULAR_DEFAULT_MESSAGES.AMOUNT_PAYABLE
  );
  const giftCardOtherPaymentMessage = getStringFromId(
    VERNACULAR_STRING_IDS.GIFT_CARD_OTHER_PAYMENT_MESSAGE,
    VERNACULAR_DEFAULT_MESSAGES.GIFT_CARD_OTHER_PAYMENT_MESSAGE
  );

  /**
   * @description to get the gift card number format. format is xxxx xxxx xxxx xxxx
   * @param value
   * @returns string
   */
  const giftCardNumberFormat = (value: string) => value?.replace(/\d{4}(?=.)/g, "$& ");

  const config = {
    title,
    titleFont: deviceType === DEVICE_TYPE.DESKTOP ? theme.typography.sectionTitle : theme.typography.subTitleLG,
    subTitle,
    leadingIcon: getIcons(platform, ICONS_NAMES.GIFT_CARD),
    toggleAccordian: () => toggleNewPaymentAccordian(ACCORDIAN.GIFT_CARD, isDisabled),
    isAccordianOpened,
    endIcon,
    giftCardNumber: giftCardNumberFormat(giftCardNumber.value),
    giftCardPin: giftCardPin.value,
    error: {
      giftCardNumber: giftCardNumber.error,
      giftCardPin: giftCardPin.error,
    },
    doPayment: () => doPayment(ACCORDIAN.GIFT_CARD),
    handleGiftCardSubmit,
    offerText: "",
    giftCardNumberLabelText,
    giftCardPinLabelText,
    payNowText: submitText,
    payNowActionText: payNowText,
    placeOrderText,
    onInputChange: handleInputChange,
    handleInputBlur: validateInputs,
    isInputDisabled: !!isGiftCardApplied,
    inputIconText: isGiftCardApplied ? remove : "",
    onInputIconClick: isGiftCardApplied ? handleGiftCardSubmit : null,
    isInputIconActive: isGiftCardApplied,
    isInputIconVisible: isGiftCardApplied,
    giftCardValue: balance,
    giftCardAmountUsed,
    giftCardBalanceRemaining,
    walletAmountUsed: isWalletChecked ? walletBalance : 0,
    walletUsedText,
    giftCardValueText,
    giftCardAmountUsedText,
    giftCardBalanceRemainingText,
    amountPayable: finalOrderAmount,
    amountPayableText,
    isFilledButtonVisible: !!(finalOrderAmount === 0),
    message: {
      info: true,
      text: giftCardOtherPaymentMessage,
      body: "bodySmall",
    },
    // handle scroll to first method
    doPaymentWithOtherMethod,
    isDisabled,
    theme,
    giftCardPinInputFieldId: "gift-card-pin",
  };

  if (deviceType === DEVICE_TYPE.DESKTOP) {
    return <GiftCardDesktop {...config} />;
  }
  return <GiftCard {...config} theme={theme} />;
};

export default GiftcardWrapper;
